// Hint finger targets (IMPLEMENTATION §3 M7 "HintFinger driven by sim tutorialView", UX §4 "Hint rules").
// PURE: which gesture the finger demonstrates for the active tutorial tip, in client CSS px — read from the
// sim state (query views only) and the shell's rects. The tip text itself is copy (≤ 6 words, verb first).
//   tapToFire        tap the Gallery
//   tapVein / holdToDig  tap the Lead Vein (Play only; holdToDig only while the goal is still u05 — later
//                    goals have their own hints, playtest fix)
//   mergeToMakeRoom  drag a loose item onto a merge partner (query.mergePartners)
//   dragToSell       drag the loose item the counter pays most for (the sim's counter drop) to Sarge
//   dropOnVein       tap-placing the Lead Vein: tap the vein; Shop closed: tap the Shop tab; the card's
//                    pop-over open: tap Place; open: drag the Lead Vein card onto the vein
//   pickChute        Play: tap the Build tab; Build: tap the Chute tool
// ghostGesture (the goal's item placed as a ghost, goalView.ghost; playtest fix): drag a needed item onto
// the ghost, else merge two smaller ones of its family, else tap the vein that digs it, else tap the ghost.
// null = nothing on screen to point at (the text still shows).
import type { Mode, Tool } from '../input/modes.ts';
import { cellCenter, type Layout, type Rect } from '../layout/computeLayout.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { ShopId, TipId } from '../sim/gen/ids.ts';
import { type GoalGhost, mergePartners, resolveDrop } from '../sim/query.ts';
import type { FloorItem, SimState } from '../sim/state.ts';

export interface Pt {
  x: number;
  y: number;
}

export type HintGesture = { k: 'tap'; at: Pt } | { k: 'drag'; from: Pt; to: Pt };

/** DOM rects the finger may point at (null when not on screen). */
export type HintRectId =
  | 'counter'
  | 'shopTab'
  | 'buildTab'
  | 'chuteTool'
  | 'leadCard'
  | 'placeBtn'
  | 'sarge';

export interface HintCtx {
  s: SimState;
  d: SimDefs;
  layout: Layout;
  mode: Mode;
  tool: Tool;
  shopOpen: boolean;
  /** A Shop card's pop-over (with its Place button) is open. */
  popover?: boolean;
  /** The Shop item being tap-placed (a Shop selection), else null. */
  placing?: ShopId | null;
  rect(id: HintRectId): Rect | null;
}

const centre = (r: Rect): Pt => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });

function leadVein(s: SimState): { x: number; y: number } | null {
  for (const b of s.buildings) if (b.type === 'deposit' && b.ore === 'lead') return b;
  return null;
}

/** Two loose items that merge (query.mergePartners), the first loose item with a partner and its lowest. */
function twins(s: SimState, d: SimDefs): [FloorItem, FloorItem] | null {
  for (const a of s.floor) {
    const id = mergePartners(s, d, a.id)[0];
    const b = id === undefined ? undefined : s.floor.find((x) => x.id === id);
    if (b) return [a, b];
  }
  return null;
}

/** The loose item the counter would pay most for (the sim's counter drop, R13; pinned items never). */
function mostValuable(s: SimState, d: SimDefs): FloorItem | null {
  let best: FloorItem | null = null;
  let bestV = -1;
  for (const it of s.floor) {
    const out = resolveDrop(
      s,
      d,
      { k: 'cell', x: it.x, y: it.y, id: it.id },
      { k: 'counter' },
      false,
    );
    if (out.k !== 'sell') continue;
    if (out.value > bestV) {
      best = it;
      bestV = out.value;
    }
  }
  return best;
}

export function hintGesture(tip: TipId, c: HintCtx): HintGesture | null {
  const cell = (x: number, y: number): Pt => cellCenter(c.layout, x, y);
  switch (tip) {
    case 'tapToFire': {
      const g = c.s.buildings.find((b) => b.type === 'gallery');
      return g ? { k: 'tap', at: cell(g.x, g.y) } : null;
    }
    case 'tapVein':
    case 'holdToDig': {
      // Build mode: a tap on the vein selects it; after u05 the Crate / later goals lead (playtest fix)
      if (c.mode === 'build') return null;
      if (tip === 'holdToDig' && c.d.unlocks[c.s.progress.goal]?.id !== 'u05') return null;
      const v = leadVein(c.s);
      return v ? { k: 'tap', at: cell(v.x, v.y) } : null;
    }
    case 'mergeToMakeRoom': {
      const t = twins(c.s, c.d);
      return t ? { k: 'drag', from: cell(t[0].x, t[0].y), to: cell(t[1].x, t[1].y) } : null;
    }
    case 'dragToSell': {
      const it = mostValuable(c.s, c.d);
      const r = c.rect('counter');
      return it && r ? { k: 'drag', from: cell(it.x, it.y), to: centre(r) } : null;
    }
    case 'dropOnVein': {
      const v = leadVein(c.s);
      if (c.placing === 'deposit.lead') return v ? { k: 'tap', at: cell(v.x, v.y) } : null;
      if (!c.shopOpen) {
        const r = c.rect('shopTab');
        return r ? { k: 'tap', at: centre(r) } : null;
      }
      if (c.popover) {
        const r = c.rect('placeBtn');
        return r ? { k: 'tap', at: centre(r) } : null;
      }
      const card = c.rect('leadCard');
      return v && card ? { k: 'drag', from: centre(card), to: cell(v.x, v.y) } : null;
    }
    case 'pickChute': {
      const r = c.mode === 'build' ? c.rect('chuteTool') : c.rect('buildTab');
      if (c.mode === 'build' && c.tool === 'chute') return null;
      return r ? { k: 'tap', at: centre(r) } : null;
    }
    default:
      return null;
  }
}

/** The hint for a goal item placed as a ghost (see the header). */
export function ghostGesture(g: GoalGhost, c: HintCtx): HintGesture | null {
  const cell = (x: number, y: number): Pt => cellCenter(c.layout, x, y);
  const at = cell(g.x, g.y);
  const need = g.rows.find((r) => r.filled < r.n);
  if (need === undefined) return null;
  const ready = c.s.floor.find((it) => it.fam === need.fam && it.tier === need.tier);
  if (ready) return { k: 'drag', from: cell(ready.x, ready.y), to: at };
  for (const a of c.s.floor) {
    if (a.fam !== need.fam || a.tier >= need.tier) continue;
    const id = mergePartners(c.s, c.d, a.id)[0];
    const b = id === undefined ? undefined : c.s.floor.find((x) => x.id === id);
    if (b) return { k: 'drag', from: cell(a.x, a.y), to: cell(b.x, b.y) };
  }
  if (c.mode !== 'build') {
    const v = c.s.buildings.find(
      (b) => b.type === 'deposit' && b.ghost === null && `ore_${b.ore}` === need.fam,
    );
    if (v) return { k: 'tap', at: cell(v.x, v.y) };
  }
  return { k: 'tap', at };
}

/** Words in a hint (UX §4: at most 6, verb first). */
export function wordCount(text: string): number {
  return text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}
